import {Box, BoxProps, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import katex from 'katex';
import {lazy, Suspense} from 'react';
import ReactMarkdown, {Components} from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import PebblePlotRenderer from '../PebblePlotRenderer/PebblePlotRenderer';

import 'katex/dist/katex.min.css';

// Lazy load ObservableWithPlotlyJson (uses Plotly JSON spec, renders with Observable Plot, ~50KB)
const ObservableWithPlotlyJson = lazy(() => import('./ObservableWithPlotlyJson'));

// Lazy load PebblePlotRenderer (uses PebblePlot JSON spec)
// const PebblePlotRenderer = lazy(() => import('../PebblePlotRenderer/PebblePlotRenderer'));

export interface MarkdownContentRendererProps extends BoxProps {
  content: string;
}

const StyledMarkdownBox = styled(Box)(({theme}) => ({
  '& h1': {
    fontSize: '2rem',
    fontWeight: 700,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  '& h2': {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  '& h2:not(:first-of-type)': {
    marginTop: theme.spacing(4),
  },
  '& h3': {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& h4, & h5, & h6': {
    fontSize: '1.1rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& p': {
    lineHeight: 1.8,
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  '& ul, & ol': {
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
  },
  '& code': {
    backgroundColor: theme.palette.grey[200],
    padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
    borderRadius: theme.spacing(0.5),
    fontFamily: 'monospace',
    fontSize: '0.875em',
  },
  '& pre': {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    overflow: 'auto',
    marginBottom: theme.spacing(2),
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontStyle: 'italic',
    backgroundColor: theme.palette.grey[50],
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    marginBottom: theme.spacing(2),
    display: 'table',
    border: `1px solid ${theme.palette.divider}`,
  },
  '& thead': {
    display: 'table-header-group',
  },
  '& tbody': {
    display: 'table-row-group',
  },
  '& tr': {
    display: 'table-row',
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    display: 'table-cell',
  },
  '& th': {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
    textAlign: 'left',
  },
  '& td': {
    textAlign: 'left',
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  '& video': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[900],
  },
  '& iframe': {
    maxWidth: '100%',
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: 'none',
    aspectRatio: '16 / 9',
    width: '100%',
    height: 'auto',
    minHeight: '315px',
  },
}));

/**
 * Custom code component that renders graphs for plotly code blocks and math equations
 */
const CodeComponent: Components['code'] = ({node, className, children, ...props}) => {
  const inline = (props as {inline?: boolean}).inline ?? false;
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  // If it's a plotly code block, render the ObservableWithPlotlyJson component
  // Uses Plotly.js JSON specification but renders with Observable Plot
  if (!inline && language === 'plotly') {
    const jsonString = String(children).replace(/\n$/, '');
    return (
      <Suspense fallback={<Box sx={{p: 2, textAlign: 'center'}}>Loading chart...</Box>}>
        <ObservableWithPlotlyJson jsonString={jsonString} />
      </Suspense>
    );
  }

  // If it's a pebbleplot code block, render the PebblePlotRenderer component
  if (!inline && language === 'pebbleplot') {
    const jsonString = String(children).replace(/\n$/, '');
    return (
      <Suspense fallback={<Box sx={{p: 2, textAlign: 'center'}}>Loading chart...</Box>}>
        <PebblePlotRenderer jsonString={jsonString} />
      </Suspense>
    );
  }

  // If it's a math code block, render using KaTeX
  if (!inline && (language === 'math' || language === 'latex' || language === 'katex')) {
    const mathContent = String(children).replace(/\n$/, '').trim();
    try {
      const html = katex.renderToString(mathContent, {
        throwOnError: false,
        displayMode: true,
      });
      return (
        <Box
          data-testid="math-code-block"
          sx={(theme) => ({
            backgroundColor: theme.palette.grey[100],
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            borderRadius: theme.spacing(1),
            overflow: 'auto',
            '& .katex': {
              fontSize: '1.1em',
            },
            '& > .katex-display': {
              margin: theme.spacing(1),
            },
          })}
          dangerouslySetInnerHTML={{__html: html}}
        />
      );
    } catch {
      // Fallback to plain code if KaTeX rendering fails
      return (
        <pre {...(props as React.JSX.IntrinsicElements['pre'])}>
          <code className={className}>{mathContent}</code>
        </pre>
      );
    }
  }

  // Default code rendering
  const code = String(children).replace(/\n$/, '');
  if (inline) {
    return (
      <code className={className} {...props}>
        {code}
      </code>
    );
  }

  return (
    <pre {...(props as React.JSX.IntrinsicElements['pre'])}>
      <code className={className}>{code}</code>
    </pre>
  );
};

/**
 * Custom video component that renders video elements safely
 * Supports regular video files and multiple resolutions
 * When multiple sources are provided, the browser automatically selects the best one
 */
const VideoComponent = (props: React.JSX.IntrinsicElements['video']) => {
  const src = props.src;
  const hasChildren = Boolean(props.children);

  // Return null only if there's no src AND no children (multiple sources)
  if (!src && !hasChildren) return null;

  return (
    <Box
      component="video"
      controls
      width="100%"
      {...props}
      sx={{
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 1,
        my: 2,
        backgroundColor: 'grey.900',
      }}
    />
  );
};

/**
 * Custom iframe component that renders YouTube and Rumble embeds safely
 * Supports regular YouTube videos and Shorts (both use youtube.com/embed format)
 * Supports Rumble videos (rumble.com/embed format)
 */
const IframeComponent = (props: React.JSX.IntrinsicElements['iframe']) => {
  const src = props.src;
  if (!src) return null;

  // Allow YouTube embeds (regular videos and Shorts both use youtube.com/embed format)
  const isYouTube = src.includes('youtube.com/embed') || src.includes('youtu.be');
  // Allow Rumble embeds
  const isRumble = src.includes('rumble.com/embed');

  if (!isYouTube && !isRumble) return null;

  return (
    <Box
      component="iframe"
      {...props}
      sx={{
        maxWidth: '100%',
        borderRadius: 1,
        my: 2,
        border: 'none',
        aspectRatio: '16 / 9',
        width: '100%',
        height: 'auto',
        minHeight: '315px',
      }}
    />
  );
};

/**
 *
 * @deprecated move to @coolcolduk/ui
 * @returns
 */
export default function MarkdownContentRenderer({content, sx, ...boxProps}: MarkdownContentRendererProps) {
  if (!content || content.trim().length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{fontStyle: 'italic'}}>
        No content available
      </Typography>
    );
  }

  const components: Components = {
    code: CodeComponent,
    video: VideoComponent as Components['video'],
    iframe: IframeComponent as Components['iframe'],
  };

  return (
    <StyledMarkdownBox sx={sx} {...boxProps}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </StyledMarkdownBox>
  );
}
